import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePackages, usePackageMutations } from '@/src/hooks/admin/usePackages';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Package, Plus, Search, Edit, Trash2, Eye, Lock } from 'lucide-react';
import type { CreditPackage } from '@/src/types/admin/package';
import { isProtectedPackage } from '@/src/constants/admin/packages';

export default function PackageList() {
  const navigate = useNavigate();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<CreditPackage | null>(null);
  const [forceDelete, setForceDelete] = useState(false);

  const { packages, loading, refetch } = usePackages(includeInactive);
  const { deletePackage, toggleActive, loading: mutationLoading } = usePackageMutations();

  // Filter packages by search query
  const filteredPackages = packages.filter(
    (pkg) =>
      pkg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pkg.package_code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDelete = async () => {
    if (!packageToDelete) return;
    // Extra guard: block deletion of protected packages
    if (isProtectedPackage(packageToDelete.package_code)) return;

    try {
      await deletePackage(packageToDelete.id, forceDelete, packageToDelete.package_code);
      setDeleteDialogOpen(false);
      setPackageToDelete(null);
      setForceDelete(false);
      refetch();
    } catch (error) {
      // Error already handled in hook
    }
  };

  const handleToggleActive = async (pkg: CreditPackage) => {
    // Block deactivation of protected packages
    if (isProtectedPackage(pkg.package_code) && pkg.is_active) return;
    try {
      await toggleActive(pkg.id, !pkg.is_active, pkg.package_code);
      refetch();
    } catch (error) {
      // Error already handled in hook
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
    }).format(price);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">จัดการแพ็กเกจ</h1>
          <p className="text-muted-foreground">
            สร้าง แก้ไข และจัดการแพ็กเกจ Credit สำหรับระบบ
          </p>
        </div>
        <Button onClick={() => navigate('/admin/packages/new')}>
          <Plus className="mr-2 h-4 w-4" />
          สร้างแพ็กเกจใหม่
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="ค้นหาชื่อหรือรหัสแพ็กเกจ..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="include-inactive" className="text-sm text-muted-foreground">
                แสดง inactive
              </label>
              <Switch
                id="include-inactive"
                checked={includeInactive}
                onCheckedChange={setIncludeInactive}
              />
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            รายการแพ็กเกจ ({filteredPackages.length})
          </CardTitle>
          <CardDescription>
            จัดการแพ็กเกจ Credit และราคาของระบบ
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="text-muted-foreground">กำลังโหลด...</div>
            </div>
          ) : filteredPackages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">ไม่พบแพ็กเกจ</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery
                  ? 'ไม่พบแพ็กเกจที่ตรงกับคำค้นหา'
                  : 'ยังไม่มีแพ็กเกจในระบบ เริ่มสร้างแพ็กเกจแรกได้เลย'}
              </p>
              {!searchQuery && (
                <Button onClick={() => navigate('/admin/packages/new')}>
                  <Plus className="mr-2 h-4 w-4" />
                  สร้างแพ็กเกจแรก
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>รหัส</TableHead>
                  <TableHead>ชื่อแพ็กเกจ</TableHead>
                  <TableHead className="text-right">ราคา/เดือน</TableHead>
                  <TableHead className="text-right">Credits</TableHead>
                  <TableHead className="text-center">สถานะ</TableHead>
                  <TableHead className="text-right">จัดการ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPackages.map((pkg) => (
                  <TableRow key={pkg.id}>
                    <TableCell className="font-medium">{pkg.package_code}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => navigate(`/admin/packages/${pkg.id}`)}
                        className="text-left hover:underline focus:outline-none"
                      >
                        <div className="font-medium">{pkg.name}</div>
                        {pkg.description && (
                          <div className="text-sm text-muted-foreground line-clamp-1">
                            {pkg.description}
                          </div>
                        )}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      {formatPrice(pkg.monthly_price)}
                    </TableCell>
                    <TableCell className="text-right">
                      {pkg.included_credits === -1
                        ? 'Unlimited'
                        : pkg.included_credits.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center gap-1">
                        <Switch
                          checked={pkg.is_active}
                          onCheckedChange={() => handleToggleActive(pkg)}
                          disabled={mutationLoading || (isProtectedPackage(pkg.package_code) && pkg.is_active)}
                          title={
                            isProtectedPackage(pkg.package_code)
                              ? 'ไม่สามารถปิดใช้งาน Protected Package ได้'
                              : undefined
                          }
                        />
                        {isProtectedPackage(pkg.package_code) && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Lock className="h-3 w-3" /> Protected
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/packages/${pkg.id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/packages/${pkg.id}/edit`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (isProtectedPackage(pkg.package_code)) return;
                            setPackageToDelete(pkg);
                            setDeleteDialogOpen(true);
                          }}
                          disabled={isProtectedPackage(pkg.package_code) || mutationLoading}
                          title={
                            isProtectedPackage(pkg.package_code)
                              ? 'ไม่สามารถลบ Protected Package ได้'
                              : 'ลบแพ็กเกจ'
                          }
                        >
                          <Trash2 className={`h-4 w-4 ${isProtectedPackage(pkg.package_code) ? 'text-gray-300' : 'text-red-600'}`} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการลบแพ็กเกจ</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <div>คุณแน่ใจหรือไม่ที่จะลบแพ็กเกจ <strong>{packageToDelete?.name}</strong>?</div>
                {packageToDelete && isProtectedPackage(packageToDelete.package_code) ? (
                  <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                    <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>ไม่สามารถลบได้</strong> — แพ็กเกจนี้เป็น Protected Package ของระบบ
                      จึงไม่สามารถลบหรือปิดใช้งานได้
                    </span>
                  </div>
                ) : (
                  <div className="text-sm">
                    หากมีลูกค้าที่ใช้แพ็กเกจนี้อยู่ คุณต้องเลือก "ลบแบบบังคับ" เพื่อดำเนินการต่อ
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {(!packageToDelete || !isProtectedPackage(packageToDelete.package_code)) && (
            <div className="flex items-start space-x-3 rounded-md border p-4">
              <Checkbox
                id="force-delete-list"
                checked={forceDelete}
                onCheckedChange={(checked) => setForceDelete(checked === true)}
              />
              <div className="space-y-1 leading-none">
                <label
                  htmlFor="force-delete-list"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  ลบแบบบังคับ
                </label>
                <p className="text-sm text-muted-foreground">
                  ลบถึงแม้จะมีลูกค้าใช้งานแพ็กเกจนี้อยู่
                </p>
              </div>
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={mutationLoading}
              onClick={() => {
                setPackageToDelete(null);
                setForceDelete(false);
              }}
            >
              {packageToDelete && isProtectedPackage(packageToDelete.package_code) ? 'ปิด' : 'ยกเลิก'}
            </AlertDialogCancel>
            {(!packageToDelete || !isProtectedPackage(packageToDelete.package_code)) && (
              <AlertDialogAction
                onClick={handleDelete}
                disabled={mutationLoading}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-500 focus:ring-2 focus:ring-offset-2 font-semibold"
                autoFocus
              >
                {mutationLoading ? 'กำลังลบ...' : '⚠️ เข้าใจแล้ว ดำเนินการต่อ'}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
